import type { IntlShape } from 'react-intl-next';

import type {
	Command,
	CommandDispatch,
	DropdownOptionT,
	FloatingToolbarButton,
	FloatingToolbarDropdown,
} from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import { CONFIG_ITEM_KEYS } from '../../config-items/config-item-keys';
import type {
	AIGlobalOptIn,
	EditorPluginAIProvider,
	SelectionToolbarButton,
	SelectionToolbarDropdown,
	SelectionToolbarDropdownItem,
	SelectionToolbarItem,
} from '../../types';
import { createOpenAIModalCommand } from '../decoration/actions';
import { getAgentsDropdownOptions } from '../rovo-agents/agents-dropdown';

function isSelectionToolbarDropdown(item: SelectionToolbarItem): item is SelectionToolbarDropdown {
	return item.type === 'dropdown';
}

function isSelectionToolbarButton(item: SelectionToolbarItem): item is SelectionToolbarButton {
	return item.type === 'button';
}

export const getSelectionToolbarItems = ({
	selectionToolbarItems,
	intl,
	aiGlobalOptIn,
	state,
	editorPluginAIProvider,
}: {
	selectionToolbarItems?: SelectionToolbarItem[];
	intl: IntlShape;
	aiGlobalOptIn: AIGlobalOptIn;
	state: EditorState;
	editorPluginAIProvider: EditorPluginAIProvider;
}) => {
	if (!selectionToolbarItems) {
		return [];
	}

	let didMouseEnterMap: { [id: string]: boolean } = {};

	const dropdownItems = selectionToolbarItems.map(
		(itemConfig: SelectionToolbarItem, index: number) => {
			if (isSelectionToolbarButton(itemConfig)) {
				didMouseEnterMap[itemConfig.id] = false;
				return {
					type: 'button',
					showTitle: !!itemConfig.title,
					testId: itemConfig.id,
					icon: itemConfig.icon,
					...(itemConfig.title && {
						title: intl.formatMessage(itemConfig.title),
					}),
					// to confirm tooltip
					tooltipContent: intl.formatMessage(sharedMessages.aiUniversalShortCallToAction),
					disabled: false,
					onMouseEnter: () => {
						didMouseEnterMap[itemConfig.id] = true;
						return false;
					},
					onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
						const openAIModalCommand = createOpenAIModalCommand({
							state,
							configItem: itemConfig.configItem,
							lastTriggeredFrom: 'floatingToolbar',
							// ED-18194 Added analytics for floating toolbar to determine
							// the % of users who click the button vs shift tab to it
							triggerMethod: !didMouseEnterMap[itemConfig.id] ? 'keyboard' : 'click',
							aiGlobalOptIn,
						});

						openAIModalCommand(state, dispatch);
						return false;
					},
				} as FloatingToolbarButton<Command>;
			}

			if (isSelectionToolbarDropdown(itemConfig)) {
				const options = itemConfig.options
					? ([
							{
								title: 'Suggested prompts',
								disabled: true,
								testId: 'ak-editor-ai-selection-toolbar-dropdown-heading',
							},
							...itemConfig.options.map((option: SelectionToolbarDropdownItem, index: number) => {
								let icon = option.icon?.();
								if (
									!icon &&
									option.configItem.icon &&
									option.configItem.key !== CONFIG_ITEM_KEYS.FREE_GENERATE
								) {
									icon = option.configItem.icon?.({
										formatMessage: intl.formatMessage,
										shownAt: 'suggestions',
									});
								}

								return {
									title: intl.formatMessage(option.label),
									testId: `ak-editor-ai-selection-toolbar-${itemConfig.id}-button-${index}`,
									//Note: free generate icon has built in rainbow border in svg, for it we need to use separate `...` icon
									icon,
									onClick: (state: EditorState, dispatch?: CommandDispatch, view?: EditorView) => {
										const openAIModalCommand = createOpenAIModalCommand({
											state,
											configItem: option.configItem,
											lastTriggeredFrom: 'floatingToolbarDropdown',
											// ED-18194 Added analytics for floating toolbar to determine
											// the % of users who click the button vs shift tab to it
											// triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
											aiGlobalOptIn,
										});

										openAIModalCommand(state, dispatch);
										return false;
									},
								};
							}),
						] as Array<DropdownOptionT<Command>>)
					: [];

				// append rogo avents to dropdown options
				if (itemConfig.includeAgents) {
					const rovoDropdownOptions = getAgentsDropdownOptions({
						intl,
						state,
						aiGlobalOptIn,
						editorPluginAIProvider,
					});

					options.push({
						title: 'Agents',
						disabled: true,
						testId: 'ak-editor-ai-selection-toolbar-dropdown-heading',
						onClick: () => false,
					});
					options.push(...rovoDropdownOptions);
				}

				return {
					type: 'dropdown',
					testId: itemConfig.id,
					title: itemConfig.label ? intl.formatMessage(itemConfig.label) : '',
					disabled: false,
					showTitle: !!itemConfig.label,
					dropdownWidth: 240, // can be based on longest item
					options,
				};
			}
		},
	);

	return dropdownItems as FloatingToolbarDropdown<Command>[];
};
